import React, { useState } from "react";
import { IProject } from "features/settings/types";
import { Dropdown, Popconfirm, Skeleton, Modal } from "antd";
import { useDelete } from "hooks/useDelete";
import { QUERY_KEY_FOR_TALENT_PROFILE } from "features/settings/hooks/useGetTalentProfile";
import { AddProject } from "./AddProject";
import { ProfileNavigationButtons } from "./ProfileNavigationButtons";
import { useNavigate } from "react-router-dom";
import {
  getNextSection,
  isLastSection,
} from "../../services/profileNavigationService";

const deleteEndPointUrl = "delete-project/";

export const Projects: React.FC<{
  data: IProject[];
  isLoading?: boolean;
}> = ({ data, isLoading }) => {
  const navigate = useNavigate();
  const [addHistory, setAddHistory] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const { removeData } = useDelete({
    queryKey: QUERY_KEY_FOR_TALENT_PROFILE,
    deleteEndPointUrl,
  });

  const handleNext = () => {
    const nextSection = getNextSection(window.location.pathname);
    if (nextSection) {
      navigate(nextSection.link);
    }
  };

  const handleCompleteProfile = () => {
    setShowCongratulations(true);
    setTimeout(() => {
      setShowCongratulations(false);
    }, 4000);
  };

  const isLast = isLastSection(window.location.pathname);

  return (
    <>
      <AddProject open={addHistory} handleClose={() => setAddHistory(false)} />

      <div className="mt-6 max-w-5xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-base font-semibold text-primary">Projects</h4>
            <p className="text-sm text-gray-500">Add your past projects</p>
          </div>

          <button
            onClick={() => setAddHistory(true)}
            className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-md flex items-center gap-1 hover:bg-primary/90"
          >
            <i className="ri-add-line"></i>
            <span>Add New</span>
          </button>
        </div>

        <Skeleton active loading={isLoading}>
          <div className="space-y-4">
            {data.map((item) => (
              <Dropdown
                key={item.id}
                trigger={["click"]}
                dropdownRender={() => (
                  <div className="bg-gray-50 rounded-lg shadow-lg border px-4 py-3 text-gray-600 max-w-md">
                    <p className="text-sm mb-1">
                      <span className="font-medium">Title:</span> {item.title}
                    </p>

                    <p className="text-sm mb-1">
                      <span className="font-medium">Description:</span>{" "}
                      {item.description}
                    </p>

                    <p className="text-sm mb-1">
                      <span className="font-medium">Type:</span> {item.type}
                    </p>

                    <p className="text-sm mb-1">
                      <span className="font-medium">Role:</span> {item.role}
                    </p>

                    {item.link && (
                      <p className="text-sm">
                        <span className="font-medium">Project Link:</span>{" "}
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >
                          {item.link}
                        </a>
                      </p>
                    )}
                  </div>
                )}
              >
                <div className="border border-gray-200 bg-white rounded-lg px-4 py-4 cursor-pointer hover:border-secondary transition">
                  <div className="flex justify-between items-start">
                    <h4 className="text-sm font-semibold text-primary capitalize">
                      {item.title}
                    </h4>

                    <Popconfirm
                      okButtonProps={{
                        style: {
                          backgroundColor: "#f5222d",
                          color: "#ffffff",
                        },
                      }}
                      title="Delete project"
                      description="Are you sure you want to delete this project?"
                      onConfirm={() => removeData(item.id)}
                    >
                      <i className="ri-delete-bin-line text-gray-400 cursor-pointer text-xl hover:text-red-600"></i>
                    </Popconfirm>
                  </div>
                </div>
              </Dropdown>
            ))}
          </div>
        </Skeleton>

        <div className="mt-6">
          <ProfileNavigationButtons
            hasForm={false}
            onNext={isLast ? handleCompleteProfile : handleNext}
            nextButtonLabel={isLast ? "Complete Profile" : "Next"}
          />
        </div>
      </div>

      <Modal
        open={showCongratulations}
        footer={null}
        closable={false}
        centered
        width={500}
        bodyStyle={{ padding: "32px" }}
      >
        <div className="text-center">
          <div className="mb-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <i className="ri-check-line text-green-600 text-4xl"></i>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-primary mb-3">
            Profile Complete! 🎉
          </h2>
          <p className="text-gray-600 mb-2">
            Congratulations! You've successfully completed your profile.
          </p>
          <p className="text-gray-500 text-sm">
            Recruiters can now view your complete profile and match you with
            relevant opportunities.
          </p>
        </div>
      </Modal>
    </>
  );
};
